import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Module Key Schema - modules the chat agent can operate in
 */
export const ModuleKeySchema = z.enum([
  "home",
  "workflows",
  "teams",
  "query-builder",
  "template-blocks",
  "email-templates",
  "pdf-templates",
  "brand-kit",
  "wizard",
  "collections",
  "edi",
  "procedures",
  "forecast",
  "cut-to-length",
  "ap-command-center",
  "tasks",
  "projects",
  "workbooks",
  "oms",
  "schema-labeling",
  "user-sessions",
  "accounts-payable",
  "accounts-receivable",
  "apps",
]);

/**
 * Page Key Schema - finer-grained pages within a module that contribute
 * page-specific prompt fragments and tools. The backend page registry must
 * have an entry for every value here (enforced via Record<PageKey, ...>).
 */
export const PageKeySchema = z.enum([
  "workflow-editor",
  "workflow-detail",
  "sales-orders",
  "famous-edi-invoices",
  "famous-edi-order-confirmations",
  "famous-edi-shipments",
  "procedure-editor",
  // OMS v3 surfaces (the `oms` chat module). `sales-orders` above is the
  // legacy v2 list under `edi`.
  "oms-sales-orders",
  "oms-sales-order",
  "oms-discounts",
  "oms-discount-group",
  "oms-customers",
  "oms-price-lists",
  "oms-product-catalog",
  "oms-product-matches",
  "oms-quote-builder",
  "schema-labeling-review",
  // Accounts Payable surfaces (the `accounts-payable` chat module).
  "ap-invoices",
  "ap-invoice",
  "ap-payments",
  "ap-purchase-orders",
  // Accounts Receivable surfaces (the `accounts-receivable` chat module).
  "ar-cash-dashboard",
  "ar-cash-receipt",
]);

/** The OMS v3 page keys, in one place for the harness module's `chatPages`. */
export const OMS_PAGE_KEYS = [
  "oms-sales-orders",
  "oms-sales-order",
  "oms-discounts",
  "oms-discount-group",
  "oms-customers",
  "oms-price-lists",
  "oms-product-catalog",
  "oms-product-matches",
  "oms-quote-builder",
] as const satisfies readonly z.infer<typeof PageKeySchema>[];

/** The Accounts Receivable page keys, for the harness module's `chatPages`. */
export const AR_PAGE_KEYS = [
  "ar-cash-dashboard",
  "ar-cash-receipt",
] as const satisfies readonly z.infer<typeof PageKeySchema>[];

/** The Accounts Payable page keys, for the harness module's `chatPages`. */
export const AP_PAGE_KEYS = [
  "ap-invoices",
  "ap-invoice",
  "ap-payments",
  "ap-purchase-orders",
] as const satisfies readonly z.infer<typeof PageKeySchema>[];

export const SchemaLabelingContextSchema = z.object({
  schemaId: z.uuid(),
  schemaName: z.string().max(255),
  draftId: z.uuid(),
  focusedColumn: z
    .object({
      fieldId: z.uuid(),
      fieldName: z.string().max(255),
      type: z.string().max(80),
    })
    .nullable(),
  position: z.number().int().min(0),
  total: z.number().int().min(0).max(500),
});
export type SchemaLabelingContext = z.infer<typeof SchemaLabelingContextSchema>;

/**
 * Structured content part for user messages that include image attachments.
 */
const MessageContentPartSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({
    type: z.literal("image"),
    imageData: z.string().min(1),
    mimeType: z.enum(["image/jpeg", "image/png"]),
  }),
]);

/**
 * Message Attachment Schema - file metadata attached to a chat message
 */
export const MessageAttachmentSchema = z.object({
  documentId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
});

/**
 * Message Schema - conversation messages matching AI SDK pattern.
 * User messages may include a content array when an annotated screenshot
 * is attached (text part + image part).
 */
const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.union([z.string(), z.array(MessageContentPartSchema)]),
  attachments: z.array(MessageAttachmentSchema).optional(),
});

/** How a message reached the thread: typed (absent) or spoken on a call. */
export const MessageChannelSchema = z.enum(["voice"]);

/**
 * The new user message of a chat turn. Earlier turns are rebuilt on the server
 * from the thread's saved rows, so only this message is sent.
 */
const ChatUserMessageSchema = z.object({
  content: z.union([z.string(), z.array(MessageContentPartSchema)]),
  attachments: z.array(MessageAttachmentSchema).optional(),
  channel: MessageChannelSchema.optional(),
});

/**
 * Frontend tool definition — schema sent from the frontend so the AI model
 * can call tools that execute client-side (navigation, mutations via RPC
 * hooks, etc.). The backend registers these as passthrough tools whose
 * execute returns the input args unchanged; the frontend intercepts the
 * tool-result event and runs the real handler.
 *
 * When `awaitable` is true the tool is promise-backed: the backend blocks the
 * tool's execute() until the frontend POSTs a result via submitFrontendToolResult,
 * so the agent sees the frontend-computed result in the same turn. The frontend
 * runs an awaitable tool on the `tool-call` event (not `tool-result`, which the
 * backend won't emit until the bridge resolves).
 */
export const FrontendToolSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  parameters: z.record(z.string(), z.unknown()),
  awaitable: z.boolean().optional(),
  /**
   * True when the tool has no side effects. Echoed back on the tool-call event
   * as `isWrite: false` so the chat UI can present read-only agent actions
   * (navigation, filtering) differently from mutations. Absent means unknown.
   */
  readOnlyHint: z.boolean().optional(),
});
export type FrontendTool = z.infer<typeof FrontendToolSchema>;

/**
 * Where a message sent while the thread's turn is running is slotted in
 * (AI-518): `steer` after the turn's current tool round, `followUp` once the
 * turn ends. Queued messages are placed only at those points, in ID order.
 */
export const QueueModeSchema = z.enum(["steer", "followUp"]);

/** A chat transcript's size in CSS pixels. */
export const ChatWindowSchema = z.object({
  width: z.number().int().min(1).max(20_000),
  height: z.number().int().min(1).max(20_000),
});
export type ChatWindow = z.infer<typeof ChatWindowSchema>;

/**
 * Chat Agent Chat Input Schema
 */
export const ChatAgentChatInputSchema = z.object({
  moduleKey: ModuleKeySchema,
  /**
   * Optional page-level identifier. Resolved server-side against the page
   * registry to layer page-specific prompt fragments and tools on top of
   * module defaults.
   */
  pageKey: PageKeySchema.optional(),
  currentPage: z.string(),
  message: ChatUserMessageSchema.optional(),
  /**
   * @deprecated Send `message`. Accepted for one release so open tabs keep
   * working: only its last user message is read, as the new message, and
   * every earlier entry is ignored.
   */
  messages: z.array(MessageSchema).optional(),
  threadId: z.string().uuid().optional(),
  moduleContext: z.record(z.string(), z.unknown()).optional(),
  /**
   * The chat transcript's size in CSS pixels when the message was sent, so
   * the assistant fits the views it shows to it. The panel is resizable.
   */
  chatWindow: ChatWindowSchema.optional(),
  frontendTools: z.array(FrontendToolSchema).optional(),
  /**
   * Client-generated id for this submission. A resubmit with the same id
   * returns the existing submission instead of starting a second turn.
   */
  requestId: z.uuid().optional(),
  /** Used only when the thread is busy; defaults to `followUp`. */
  queueMode: QueueModeSchema.optional(),
});

/**
 * What became of a submission: `placed` into a run, `queued` behind the
 * thread's running turn, or `withdrawn` before it was placed.
 */
export const SubmissionStatusSchema = z.enum(["queued", "placed", "withdrawn"]);

/**
 * Finish Reason Schema - reasons why the stream ended
 */
export const FinishReasonSchema = z.enum([
  "stop",
  "length",
  "tool-calls",
  "content-filter",
  "error",
  "other",
]);

/**
 * Agent Stream Event Schema - discriminated union with 6 event types
 */
export const AgentStreamEventSchema = z.discriminatedUnion("type", [
  // Text delta event - streamed text chunks
  z.object({
    type: z.literal("text-delta"),
    text: z.string(),
  }),
  // Tool call event - when agent invokes a tool
  z.object({
    type: z.literal("tool-call"),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()),
    userFacingName: z.string().optional(),
    isWrite: z.boolean().optional(),
  }),
  // Tool result event - result from tool execution
  z.object({
    type: z.literal("tool-result"),
    toolCallId: z.string(),
    toolName: z.string(),
    result: z.unknown(),
  }),
  // Thread info event - thread metadata
  z.object({
    type: z.literal("thread-info"),
    threadId: z.string().uuid(),
    isNew: z.boolean(),
    /** The detached turn's id, for `stopTurn` (chat stream only). */
    runId: z.string().uuid().optional(),
  }),
  // Finish event - stream completed
  z.object({
    type: z.literal("finish"),
    finishReason: FinishReasonSchema,
  }),
  // Assistant message persisted to DB - emitted after `finish` once the
  // assistant turn has been written to `ai_thread_messages`. Carries the
  // stable DB id (so the frontend can attach durable per-message feedback),
  // the Langfuse traceId for this turn, and the spanId of the AI SDK's
  // outermost `ai.streamText` observation (so user-feedback scores attach
  // to that specific observation rather than to the trace as a whole).
  z.object({
    type: z.literal("assistant-message-saved"),
    messageId: z.string().uuid(),
    traceId: z.string().nullable(),
    observationId: z.string().nullable(),
  }),
  // Draft snapshot event - progressive workflow draft state emitted after
  // each draft mutation tool completes, so the frontend can render the
  // workflow being built in real time.
  z.object({
    type: z.literal("draft-snapshot"),
    toolCallId: z.string(),
    draft: z.unknown(),
  }),
  // Tool input streaming event - emitted (throttled) while the model is
  // still generating a tool call's JSON input, before the `tool-call`
  // event fires. Carries the accumulated partial input text so the
  // frontend can progressively parse and render an incomplete spec (e.g.
  // streaming an email-template proposal into the editor/preview as it is
  // written). The final `tool-call`/`tool-result` remains the source of
  // truth and overwrites whatever partial state was applied.
  z.object({
    type: z.literal("tool-input-delta"),
    toolCallId: z.string(),
    toolName: z.string(),
    /** Accumulated partial JSON text of the tool input so far. */
    partialInput: z.string(),
  }),
  // Submission event - the first event of a chat stream sent with a
  // `requestId`: how the message was admitted. A `queued` or duplicate
  // submission's stream ends here; the running turn places it later.
  z.object({
    type: z.literal("submission"),
    requestId: z.uuid(),
    status: SubmissionStatusSchema,
    mode: QueueModeSchema,
    /** True when this `requestId` was already admitted: nothing new started. */
    duplicate: z.boolean(),
    threadId: z.uuid().optional(),
    runId: z.uuid().optional(),
  }),
  // Submission placed event - a queued message joined the running turn: a
  // steer after a tool round, a follow-up as the next reply. The assistant
  // text before it is final.
  z.object({
    type: z.literal("submission-placed"),
    requestId: z.uuid(),
    mode: QueueModeSchema,
    text: z.string(),
    attachments: z.array(MessageAttachmentSchema).optional(),
  }),
  // Error event - something went wrong
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
]);

/**
 * Yield Decision Schemas - telemetry for the generic "agent yields to
 * user" event. The first concrete consumer is the workflow-proposal
 * Apply Changes card; future yield kinds (approvals, clarifying
 * questions) reuse the same endpoint so yield-acceptance rate is a single
 * queryable span.
 */
export const YieldDecisionSchema = z.enum(["accepted", "discarded"]);

export const RecordYieldDecisionInputSchema = z.object({
  yieldId: z.string().min(1),
  toolCallId: z.string().min(1),
  toolName: z.string().min(1),
  decision: YieldDecisionSchema,
  threadId: z.string().uuid().optional(),
  /** Page that produced this yield, for "acceptance rate by page" queries. */
  pageKey: PageKeySchema.optional(),
});

export const RecordYieldDecisionOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Page element captured alongside a screenshot — gives the agent numeric IDs
 * it can pass directly to annotate_screen. Bounding rects are stored client-side.
 */
export const PageElementSchema = z.object({
  id: z.number().int().positive(),
  text: z.string(),
  role: z.string(),
  tag: z.string(),
  location: z.string(),
});

/**
 * Submit Screenshot Schemas - frontend delivers a captured screenshot
 * to unblock the server-side capture_screen tool's Promise bridge.
 */
export const SubmitScreenshotInputSchema = z.object({
  toolCallId: z.string().min(1),
  imageData: z.string().min(1),
  mimeType: z.enum(["image/jpeg", "image/png"]),
  elements: z.array(PageElementSchema).optional(),
});

export const SubmitScreenshotOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Submit Frontend Tool Result Schemas - generic round-trip for promise-backed
 * (awaitable) frontend tools. The frontend POSTs the handler's return value to
 * unblock the server-side passthrough tool's Promise bridge, so the agent sees
 * the result in the same turn. `result` is whatever the frontend handler returns
 * (e.g. a socket diff for a dynamic workflow-step edit).
 */
export const SubmitFrontendToolResultInputSchema = z.object({
  toolCallId: z.string().min(1),
  result: z.unknown(),
});

export const SubmitFrontendToolResultOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * How long a pending write approval stays actionable. Shared by the backend
 * pending-call expiry and the frontend approval card's local countdown so the
 * two clocks cannot drift apart.
 */
export const API_CALL_APPROVAL_TIMEOUT_MS = 180_000;

export const ApiCallDecisionInputSchema = z.object({
  pendingCallId: z.string().min(1),
});

export const ApproveApiCallOutputSchema = z.object({
  status: z.enum(["executed", "failed"]),
  resultSummary: z.unknown().optional(),
  error: z.string().optional(),
});

export const RejectApiCallOutputSchema = z.object({
  status: z.literal("rejected"),
});

/**
 * Chat History Schemas — reading back past conversations persisted in
 * `ai_threads` / `ai_thread_messages` for the signed-in user. Threads are
 * always scoped to the caller server-side; there is no cross-user access.
 */
export const ChatThreadSummarySchema = z.object({
  id: z.string().uuid(),
  /** `ai_threads.topic` when set, else derived from the first user message. */
  title: z.string(),
  /** Snippet of the most recent message, truncated server-side. */
  preview: z.string(),
  status: z.enum(["active", "closed"]),
  messageCount: z.number().int().nonnegative(),
  createdAt: z.coerce.date(),
  /** Timestamp of the newest message; null for an empty thread. */
  lastMessageAt: z.coerce.date().nullable(),
});

export const ListChatThreadsInputSchema = z.object({
  limit: z.number().int().min(1).max(50).default(20),
  offset: z.number().int().min(0).default(0),
  /** Case-insensitive substring match against message content. */
  search: z.string().trim().min(1).optional(),
});

export const ListChatThreadsOutputSchema = z.object({
  threads: z.array(ChatThreadSummarySchema),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export const GetChatThreadInputSchema = z.object({
  threadId: z.string().uuid(),
});

/**
 * A persisted tool call replayed from history. `args`/`result` are whatever
 * survived write-time redaction — history never re-derives tool data.
 */
export const ChatThreadToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
  result: z.unknown().optional(),
});

export const RecordVoiceTurnInputSchema = z.object({
  threadId: z.string().uuid().optional(),
  role: z.enum(["user", "assistant"]),
  content: z.string().trim().min(1).max(10_000),
});

export const RecordVoiceTurnOutputSchema = z.object({
  threadId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const ChatThreadMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
  createdAt: z.coerce.date(),
  toolCalls: z.array(ChatThreadToolCallSchema).optional(),
  attachments: z.array(MessageAttachmentSchema).optional(),
  channel: MessageChannelSchema.optional(),
});

export const GetChatThreadOutputSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: z.enum(["active", "closed"]),
  createdAt: z.coerce.date(),
  messages: z.array(ChatThreadMessageSchema),
});

/**
 * Fork a conversation at one of its messages (AI-525): a new thread for the
 * caller whose history is the source's up to and including `messageId`.
 */
export const ForkChatThreadInputSchema = z.object({
  threadId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const ForkChatThreadOutputSchema = z.object({
  threadId: z.string().uuid(),
});

/**
 * Live Turn Schemas — the in-progress state of a thread's current turn, kept
 * server-side so a refresh, a new tab or a dropped connection can reattach to
 * a turn that is still running (AI-520). One record per thread; each frame of
 * `subscribeTurn` is a whole snapshot, so a late joiner never needs history.
 * A turn that ended on another replica arrives without text or tools: its
 * content is in the transcript (`getThread`).
 */
export const LiveTurnStatusSchema = z.enum([
  "running",
  "complete",
  "aborted",
  "error",
  /** The process running the turn stopped heartbeating (crash or deploy). */
  "interrupted",
]);

export const LiveTurnToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  userFacingName: z.string().optional(),
  isWrite: z.boolean().optional(),
  args: z.record(z.string(), z.unknown()),
  status: z.enum(["running", "complete"]),
  /** Redacted like the chat stream; replaced by a preview when too large. */
  result: z.unknown().optional(),
  resultTruncated: z.boolean().optional(),
  startedAt: z.number(),
});

export const QueuedSubmissionSchema = z.object({
  requestId: z.uuid(),
  mode: QueueModeSchema,
  text: z.string(),
  createdAt: z.number(),
});

export const LiveTurnStateSchema = z.object({
  runId: z.string().uuid(),
  threadId: z.string().uuid(),
  status: LiveTurnStatusSchema,
  /** Bumps on every change; a frame is only sent when it moves. */
  version: z.number().int().nonnegative(),
  startedAt: z.number(),
  text: z.string(),
  toolCalls: z.array(LiveTurnToolCallSchema),
  /** Finished tool calls dropped to keep the record bounded. */
  omittedToolCalls: z.number().int().positive().optional(),
  error: z
    .object({ message: z.string(), code: z.string().optional() })
    .optional(),
  /** Set once the assistant row is final in `ai_thread_messages`. */
  assistantMessageId: z.string().uuid().optional(),
  /**
   * The submission the live text answers. It changes when a queued message
   * is placed; everything before it is then in the transcript.
   */
  requestId: z.uuid().optional(),
  /** Messages waiting for this turn, in the order they will be placed. */
  queued: z.array(QueuedSubmissionSchema).optional(),
});

export const SubscribeTurnInputSchema = z.object({
  threadId: z.string().uuid(),
});

/** `turn: null` means the thread has no turn on record. The stream ends after a terminal frame. */
export const LiveTurnFrameSchema = z.object({
  type: z.literal("snapshot"),
  turn: LiveTurnStateSchema.nullable(),
});

export const StopTurnInputSchema = z.object({
  threadId: z.string().uuid(),
  /** Stops only this run, so a late Stop can't end the turn that replaced it. */
  runId: z.string().uuid().optional(),
});

export const StopTurnOutputSchema = z.object({
  /** False when the thread had no running turn to stop. */
  stopped: z.boolean(),
});

export const WithdrawSubmissionInputSchema = z.object({
  threadId: z.uuid(),
  requestId: z.uuid(),
});

export const WithdrawSubmissionOutputSchema = z.object({
  /** False when the message was already placed (or never queued). */
  withdrawn: z.boolean(),
});

/**
 * Chat Agent Contracts
 */
export const chatAgentContracts = {
  chat: oc
    .input(ChatAgentChatInputSchema)
    .output(eventIterator(AgentStreamEventSchema))
    .route({
      method: "POST",
      path: "/chat-agent/chat",
      summary: "Send a message to the assistant",
      description:
        "Sends a user message to the in-app assistant in a new or existing thread with page context and browser tools, streaming the reply; queues it if the thread is busy.",
    }),
  recordYieldDecision: oc
    .input(RecordYieldDecisionInputSchema)
    .output(RecordYieldDecisionOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/yield-decision",
      summary: "Record a proposal accept or discard",
      description:
        "Records whether the user accepted or discarded an assistant proposal card (such as a workflow change), for acceptance-rate telemetry. Changes nothing else.",
    }),
  submitScreenshot: oc
    .input(SubmitScreenshotInputSchema)
    .output(SubmitScreenshotOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-screenshot",
      summary: "Submit a page screenshot to the assistant",
      description:
        "Delivers a browser screenshot and its numbered page elements to the assistant's pending screen-capture tool call so the turn can continue.",
    }),
  submitFrontendToolResult: oc
    .input(SubmitFrontendToolResultInputSchema)
    .output(SubmitFrontendToolResultOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-frontend-tool-result",
      summary: "Return a browser tool result to the assistant",
      description:
        "Delivers the result of an assistant tool that ran in the browser back to the waiting tool call so the assistant sees it in the same turn.",
    }),
  approveApiCall: oc
    .input(ApiCallDecisionInputSchema)
    .output(ApproveApiCallOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/approve-api-call",
      summary: "Approve an assistant write action",
      description:
        "Approves a pending change the assistant proposed and runs it as the current user; returns whether it executed or failed. Expires after three minutes.",
    }),
  rejectApiCall: oc
    .input(ApiCallDecisionInputSchema)
    .output(RejectApiCallOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/reject-api-call",
      summary: "Reject an assistant write action",
      description:
        "Declines a pending change the assistant proposed, so it is never run, and tells the assistant it was rejected.",
    }),
  listThreads: oc
    .input(ListChatThreadsInputSchema)
    .output(ListChatThreadsOutputSchema)
    .route({
      method: "GET",
      path: "/chat-agent/threads",
      summary: "List my assistant conversations",
      description:
        "Pages through the current user's past assistant conversations with title, preview, status, message count and last activity, optionally searching message text.",
    }),
  getThread: oc
    .input(GetChatThreadInputSchema)
    .output(GetChatThreadOutputSchema)
    .route({
      method: "GET",
      path: "/chat-agent/threads/{threadId}",
      summary: "Get an assistant conversation",
      description:
        "Returns one of the current user's assistant conversations with every message, attachments and the tool calls made, as recorded.",
    }),
  forkThread: oc
    .input(ForkChatThreadInputSchema)
    .output(ForkChatThreadOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/threads/{threadId}/fork",
      summary: "Fork an assistant conversation",
      description:
        "Branches a conversation at one message into a new thread for the same user, copying history up to and including that message. Returns the new thread id.",
    }),
  subscribeTurn: oc
    .input(SubscribeTurnInputSchema)
    .output(eventIterator(LiveTurnFrameSchema))
    .route({
      method: "POST",
      path: "/chat-agent/subscribe-turn",
      summary: "Follow the assistant's live reply",
      description:
        "Streams snapshots of the assistant's in-progress reply in a thread (text so far, tool calls, queued messages) until the turn finishes.",
    }),
  stopTurn: oc.input(StopTurnInputSchema).output(StopTurnOutputSchema).route({
    method: "POST",
    path: "/chat-agent/stop-turn",
    summary: "Stop the assistant's reply",
    description:
      "Stops the assistant's running turn in a thread, optionally only a specific run; returns false if nothing was running.",
  }),
  withdrawSubmission: oc
    .input(WithdrawSubmissionInputSchema)
    .output(WithdrawSubmissionOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/withdraw-submission",
      summary: "Withdraw a queued assistant message",
      description:
        "Removes a message the user queued while the assistant was busy, before it is placed in the thread; returns false if already placed.",
    }),
  /**
   * Save one spoken turn of a voice call that the assistant answered by
   * voice alone (no agent turn), so the thread holds the whole conversation.
   * Creates the thread when `threadId` is absent.
   */
  recordVoiceTurn: oc
    .input(RecordVoiceTurnInputSchema)
    .output(RecordVoiceTurnOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/record-voice-turn",
      summary: "Save a spoken voice-call turn",
      description:
        "Saves one turn of a voice call that the voice answered without the agent (user or assistant text, tagged voice) to the caller's chat thread, creating the thread if none is given; returns the thread and message ids.",
    }),
};

// Export types
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;
export type ModuleKey = z.infer<typeof ModuleKeySchema>;
export type PageKey = z.infer<typeof PageKeySchema>;
export type MessageContentPart = z.infer<typeof MessageContentPartSchema>;
export type ChatAgentChatInput = z.infer<typeof ChatAgentChatInputSchema>;
export type FinishReason = z.infer<typeof FinishReasonSchema>;
export type AgentStreamEvent = z.infer<typeof AgentStreamEventSchema>;
export type YieldDecision = z.infer<typeof YieldDecisionSchema>;
export type RecordYieldDecisionInput = z.infer<
  typeof RecordYieldDecisionInputSchema
>;
export type RecordYieldDecisionOutput = z.infer<
  typeof RecordYieldDecisionOutputSchema
>;
export type PageElement = z.infer<typeof PageElementSchema>;
export type SubmitScreenshotInput = z.infer<typeof SubmitScreenshotInputSchema>;
export type SubmitScreenshotOutput = z.infer<
  typeof SubmitScreenshotOutputSchema
>;
export type SubmitFrontendToolResultInput = z.infer<
  typeof SubmitFrontendToolResultInputSchema
>;
export type SubmitFrontendToolResultOutput = z.infer<
  typeof SubmitFrontendToolResultOutputSchema
>;
export type ChatThreadSummary = z.infer<typeof ChatThreadSummarySchema>;
export type ListChatThreadsInput = z.infer<typeof ListChatThreadsInputSchema>;
export type ListChatThreadsOutput = z.infer<typeof ListChatThreadsOutputSchema>;
export type GetChatThreadInput = z.infer<typeof GetChatThreadInputSchema>;
export type ChatThreadToolCall = z.infer<typeof ChatThreadToolCallSchema>;
export type ChatThreadMessage = z.infer<typeof ChatThreadMessageSchema>;
export type MessageChannel = z.infer<typeof MessageChannelSchema>;
export type RecordVoiceTurnInput = z.infer<typeof RecordVoiceTurnInputSchema>;
export type GetChatThreadOutput = z.infer<typeof GetChatThreadOutputSchema>;
export type ForkChatThreadInput = z.infer<typeof ForkChatThreadInputSchema>;
export type ForkChatThreadOutput = z.infer<typeof ForkChatThreadOutputSchema>;
export type LiveTurnStatus = z.infer<typeof LiveTurnStatusSchema>;
export type LiveTurnToolCall = z.infer<typeof LiveTurnToolCallSchema>;
export type LiveTurnState = z.infer<typeof LiveTurnStateSchema>;
export type LiveTurnFrame = z.infer<typeof LiveTurnFrameSchema>;
export type SubscribeTurnInput = z.infer<typeof SubscribeTurnInputSchema>;
export type StopTurnInput = z.infer<typeof StopTurnInputSchema>;
export type StopTurnOutput = z.infer<typeof StopTurnOutputSchema>;
export type QueueMode = z.infer<typeof QueueModeSchema>;
export type SubmissionStatus = z.infer<typeof SubmissionStatusSchema>;
export type QueuedSubmission = z.infer<typeof QueuedSubmissionSchema>;
export type WithdrawSubmissionInput = z.infer<
  typeof WithdrawSubmissionInputSchema
>;
export type WithdrawSubmissionOutput = z.infer<
  typeof WithdrawSubmissionOutputSchema
>;
